// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import StyledWrapper from '@insite/client-framework/Common/StyledWrapper';
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { RadioGroup, TextArea } from "@insite/mobius/index";
import Radio from '@insite/mobius/Radio';
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { initialRequestAddressFormState, RequestAddressFormState } from "@stanion/App/Components/RequestAddress/RequestAddress.form.module";
import requestAddress from '@stanion/App/Handlers/XNG/RequestAddress';
import { RequestUpdateAddressModel } from "@stanion/App/Services/RequestUpdateAddressService";
import { xMask, xScreen } from "@stanion/XDL/Utilities";
import { CreateForm } from "@stanion/XDL/Utilities/form";
import cloneDeep from "lodash/cloneDeep";
import React, { useContext, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    requestAddress,
};

interface OwnProps {
    onCancel: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onSubmit: () => void;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface RequestAddressFormStyles {
    form?: InjectableCss;
    userGridContainer?: GridContainerProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    actionButtonsGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    fieldGridItem?: GridItemProps;
    formButtonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    siteMessageStyle?: TypographyProps;
    siteMessagesGridItem?: GridItemProps;
}

export const requestAddressFormStyles: RequestAddressFormStyles = {
    form: {
        css: css`
            margin-top: 22px;
            max-width: 840px;
            ${xScreen("lte", "sm")} {
                width: 100%;
                max-width: 100%;
            }
        `,
    },
    userGridContainer: {
        gap: 27,
    },
    submitErrorGridItem: {
        width: 12,
    },
    submitErrorTitle: {
        color: "danger",
    },
    fieldGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    siteMessageStyle: {
        size: 12,
    },
    siteMessagesGridItem: {
        width: 12,
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
        `,
    },
    actionButtonsGridItem: {
        width: 12,
    },
    formButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            width: 100%;
        `,
    },
    cancelButton: {
        variant: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
};

const styles = requestAddressFormStyles;
const StyledForm = getStyledWrapper("form");

const RequestAddressForm = ({
    onSubmit,
    onCancel,
    requestAddress,
}: Props) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const toasterContext = useContext(ToasterContext);
    const formRef = useRef<HTMLFormElement>(null);
    const [requestAddressFormState, setRequestAddressFormState] = useState<RequestAddressFormState>(cloneDeep(initialRequestAddressFormState));
    const requestAddressForm = new CreateForm<RequestAddressFormState>(requestAddressFormState, setRequestAddressFormState);

    const { isSubmitted } = requestAddressForm.state;
    const fields = requestAddressForm?.state.fields;

    const onSuccessHandler = () => {
        setErrorMessage("");
        setIsSubmitting(false);
        toasterContext.addToast({
            body: siteMessage("Custom_RequestAddress_SuccessMessage"),
            messageType: "success",
        });
        onSubmit?.()
    };

    const submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!isSubmitted) {
            requestAddressForm.setSubmitted();
        }

        if (isSubmitting) {
            return;
        }

        const isFormValid = requestAddressForm.validate();
        if (!fields || !isFormValid) {
            return;
        }

        const formData = {
            acctNumber: fields.accountNumber.value,
            customerName: fields.customerName.value,
            jobName: fields.jobName.value,
            jobAddress: fields.jobAddress.value,
            jobCity: fields.jobCity.value,
            jobState: fields.jobState.value,
            jobZip: fields.jobZip.value,
            jointCheckAgreement: fields.jointCheckAgreement.value,
            checkAppropriateStatement: fields.checkAppropriateStatement.value,
            jobNumber: fields.jobNumber.value,
            defaultPoNumber: fields.defaultPoNumber.value,
            jobForemen: fields.jobForemen.value,
            projectManagerAddress: fields.projectManagerAddress.value,
            projectManagerTelephone: fields.projectManagerTelephone.value,
            projectManagerEmail: fields.projectManagerEmail.value,
            projectManager: fields.projectManager.value,
            generalContractorAddress: fields.generalContractorAddress.value,
            generalContractorTelephone: fields.generalContractorTelephone.value,
            generalContractorEmail: fields.generalContractorEmail.value,
            generalContractor: fields.generalContractor.value,
            ownerAddress: fields.ownerAddress.value,
            ownerTelephone: fields.ownerTelephone.value,
            ownerEmail: fields.ownerEmail.value,
            owner: fields.owner.value,
            address: fields.address.value,
            telephone: fields.telephone.value,
            email: fields.email.value,
            bondedJob: fields.bondedJob.value,
            bondingCompanyName: fields.bondingCompanyName.value,
            bondingCompanyPhone: fields.bondingCompanyPhone.value,
            additionalInformation: fields.additionalInformation.value,
        } as RequestUpdateAddressModel;

        setIsSubmitting(true);
        requestAddress({
            address: formData,
            onSuccess: onSuccessHandler,
            onError: (error: any) => {
                toasterContext.addToast({
                    body: siteMessage("Custom_RequestAddress_ErrorMessage"),
                    messageType: "danger",
                });
                setErrorMessage(error);
            }
        })
    };

    return (
        <StyledForm {...styles.form} onSubmit={submitHandler} noValidate ref={formRef} data-test-selector="requestAddressFormFields">
            <GridContainer {...styles.userGridContainer}>
                <GridItem {...styles.fieldGridItem}>
                    <TextField
                        value={fields.accountNumber.value}
                        onChange={e => requestAddressForm?.onFieldChange(fields.accountNumber, e.currentTarget.value)}
                        error={isSubmitted && fields.accountNumber.error}
                        {...fields.accountNumber.attrs}
                    />
                </GridItem>
                <GridItem {...styles.fieldGridItem}>
                    <TextField
                        value={fields.customerName.value}
                        onChange={e => requestAddressForm?.onFieldChange(fields.customerName, e.currentTarget.value)}
                        error={isSubmitted && fields.customerName.error}
                        {...fields.customerName.attrs}
                    />
                </GridItem>
                <GridItem {...styles.fieldGridItem}>
                    <TextField
                        value={fields.jobName.value}
                        onChange={e => requestAddressForm?.onFieldChange(fields.jobName, e.currentTarget.value)}
                        error={isSubmitted && fields.jobName.error}
                        {...fields.jobName.attrs}
                    />
                </GridItem>
                <GridItem {...styles.fieldGridItem}>
                    <TextField
                        value={fields.jobAddress.value}
                        onChange={e => requestAddressForm?.onFieldChange(fields.jobAddress, e.currentTarget.value)}
                        error={isSubmitted && fields.jobAddress.error}
                        {...fields.jobAddress.attrs}
                    />
                </GridItem>
                <GridItem {...styles.fieldGridItem}>
                    <TextField
                        value={fields.jobCity.value}
                        onChange={e => requestAddressForm?.onFieldChange(fields.jobCity, e.currentTarget.value)}
                        error={isSubmitted && fields.jobCity.error}
                        {...fields.jobCity.attrs}
                    />
                </GridItem>
                <GridItem {...styles.fieldGridItem}>
                    <TextField
                        value={fields.jobState.value}
                        onChange={e => requestAddressForm?.onFieldChange(fields.jobState, e.currentTarget.value)}
                        error={isSubmitted && fields.jobState.error}
                        {...fields.jobState.attrs}
                    />
                </GridItem>
                <GridItem {...styles.fieldGridItem}>
                    <TextField
                        value={fields.jobZip.value}
                        onChange={e => requestAddressForm?.onFieldChange(fields.jobZip, e.currentTarget.value)}
                        error={isSubmitted && fields.jobZip.error}
                        {...fields.jobZip.attrs}
                    />
                </GridItem>
                <GridItem {...styles.fieldGridItem}>
                    <TextField
                        value={fields.estimatedRequestedJob.value}
                        onChange={e => requestAddressForm?.onFieldChange(fields.estimatedRequestedJob, e.currentTarget.value)}
                        error={isSubmitted && fields.estimatedRequestedJob.error}
                        {...fields.estimatedRequestedJob.attrs}
                    />
                </GridItem>
                <GridItem {...styles.fieldGridItem}>
                    <CheckboxGroup>
                        <Checkbox
                            checked={fields.jointCheckAgreement.value}
                            onChange={(e, value) => requestAddressForm?.onFieldChange(fields.jointCheckAgreement, value)}
                            {...fields.jointCheckAgreement.attrs}
                        >
                            {translate("Joint check agreement")}
                        </Checkbox>
                    </CheckboxGroup>
                </GridItem>
                <GridItem width={12}>
                    <RadioGroup
                        value={fields.checkAppropriateStatement.value}
                        onChangeHandler={e => requestAddressForm?.onFieldChange(fields.checkAppropriateStatement, e.target.value)}
                        {...fields.checkAppropriateStatement.attrs}
                    >
                        <Radio value="Taxable">{siteMessage("Custom_RequestAddress_AppropriateStatement_Taxable")}</Radio>
                        <Radio value="Exempt">{siteMessage("Custom_RequestAddress_AppropriateStatement_Exempt")}</Radio>
                    </RadioGroup>
                </GridItem>
                <GridItem {...styles.siteMessagesGridItem}>
                    <Typography {...styles.siteMessageStyle}>{siteMessage("Custom_RequestAddress_SalesTaxMsg")}</Typography>
                    {fields.checkAppropriateStatement.value === "Exempt" &&
                        <Typography {...styles.siteMessageStyle}>{siteMessage("Custom_RequestAddress_TaxEmail")}</Typography>
                    }
                </GridItem>

                {Number(fields.estimatedRequestedJob.value || 0) >= 5000 && (
                    <>
                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                value={fields.jobNumber.value}
                                onChange={e => requestAddressForm?.onFieldChange(fields.jobNumber, e.currentTarget.value)}
                                error={isSubmitted && fields.jobNumber.error}
                                {...fields.jobNumber.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                value={fields.defaultPoNumber.value}
                                onChange={e => requestAddressForm?.onFieldChange(fields.defaultPoNumber, e.currentTarget.value)}
                                error={isSubmitted && fields.defaultPoNumber.error}
                                {...fields.defaultPoNumber.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                value={fields.jobForemen.value}
                                onChange={e => requestAddressForm?.onFieldChange(fields.jobForemen, e.currentTarget.value)}
                                error={isSubmitted && fields.jobForemen.error}
                                {...fields.jobForemen.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                value={fields.projectManagerAddress.value}
                                onChange={e => requestAddressForm?.onFieldChange(fields.projectManagerAddress, e.currentTarget.value)}
                                error={isSubmitted && fields.projectManagerAddress.error}
                                {...fields.projectManagerAddress.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                value={xMask.phoneNumberUS(fields.projectManagerTelephone.value || "")}
                                onChange={e => requestAddressForm?.onFieldChange(fields.projectManagerTelephone, e.currentTarget.value)}
                                error={isSubmitted && fields.projectManagerTelephone.error}
                                {...fields.projectManagerTelephone.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                value={fields.projectManagerEmail.value}
                                onChange={e => requestAddressForm?.onFieldChange(fields.projectManagerEmail, e.currentTarget.value)}
                                error={isSubmitted && fields.projectManagerEmail.error}
                                {...fields.projectManagerEmail.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                value={fields.projectManager.value}
                                onChange={e => requestAddressForm?.onFieldChange(fields.projectManager, e.currentTarget.value)}
                                error={isSubmitted && fields.projectManager.error}
                                {...fields.projectManager.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                value={fields.generalContractorAddress.value}
                                onChange={e => requestAddressForm?.onFieldChange(fields.generalContractorAddress, e.currentTarget.value)}
                                error={isSubmitted && fields.generalContractorAddress.error}
                                {...fields.generalContractorAddress.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                value={xMask.phoneNumberUS(fields.generalContractorTelephone.value || "")}
                                onChange={e => requestAddressForm?.onFieldChange(fields.generalContractorTelephone, e.currentTarget.value)}
                                error={isSubmitted && fields.generalContractorTelephone.error}
                                {...fields.generalContractorTelephone.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                value={fields.generalContractorEmail.value}
                                onChange={e => requestAddressForm?.onFieldChange(fields.generalContractorEmail, e.currentTarget.value)}
                                error={isSubmitted && fields.generalContractorEmail.error}
                                {...fields.generalContractorEmail.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                value={fields.generalContractor.value}
                                onChange={e => requestAddressForm?.onFieldChange(fields.generalContractor, e.currentTarget.value)}
                                error={isSubmitted && fields.generalContractor.error}
                                {...fields.generalContractor.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                value={fields.ownerAddress.value}
                                onChange={e => requestAddressForm?.onFieldChange(fields.ownerAddress, e.currentTarget.value)}
                                error={isSubmitted && fields.ownerAddress.error}
                                {...fields.ownerAddress.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                value={xMask.phoneNumberUS(fields.ownerTelephone.value || "")}
                                onChange={e => requestAddressForm?.onFieldChange(fields.ownerTelephone, e.currentTarget.value)}
                                error={isSubmitted && fields.ownerTelephone.error}
                                {...fields.ownerTelephone.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                value={fields.ownerEmail.value}
                                onChange={e => requestAddressForm?.onFieldChange(fields.ownerEmail, e.currentTarget.value)}
                                error={isSubmitted && fields.ownerEmail.error}
                                {...fields.ownerEmail.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                value={fields.owner.value}
                                onChange={e => requestAddressForm?.onFieldChange(fields.owner, e.currentTarget.value)}
                                error={isSubmitted && fields.owner.error}
                                {...fields.owner.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                value={fields.address.value}
                                onChange={e => requestAddressForm?.onFieldChange(fields.address, e.currentTarget.value)}
                                error={isSubmitted && fields.address.error}
                                {...fields.address.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                value={xMask.phoneNumberUS(fields.telephone.value || "")}
                                onChange={e => requestAddressForm?.onFieldChange(fields.telephone, e.currentTarget.value)}
                                error={isSubmitted && fields.telephone.error}
                                {...fields.telephone.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldGridItem}>
                            <TextField
                                value={fields.email.value}
                                onChange={e => requestAddressForm?.onFieldChange(fields.email, e.currentTarget.value)}
                                error={isSubmitted && fields.email.error}
                                {...fields.email.attrs}
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <RadioGroup
                                label={translate("Bonded Job?")}
                                value={fields.bondedJob.value ? "true" : "false"}
                                onChangeHandler={e => requestAddressForm?.onFieldChange(fields.bondedJob, e.target.value === "true")}
                                error={isSubmitted && fields.bondedJob.error}
                                {...fields.bondedJob.attrs}
                            >
                                <Radio value="true">{translate("Yes")}</Radio>
                                <Radio value="false">{translate("No")}</Radio>
                            </RadioGroup>
                        </GridItem>
                        {fields.bondedJob.value && (
                            <>
                                <GridItem {...styles.fieldGridItem}>
                                    <TextField
                                        value={fields.bondingCompanyName.value}
                                        onChange={e => requestAddressForm?.onFieldChange(fields.bondingCompanyName, e.currentTarget.value)}
                                        error={isSubmitted && fields.bondingCompanyName.error}
                                        {...fields.bondingCompanyName.attrs}
                                    />
                                </GridItem>
                                <GridItem {...styles.fieldGridItem}>
                                    <TextField
                                        value={xMask.phoneNumberUS(fields.bondingCompanyPhone.value || "")}
                                        onChange={e => requestAddressForm?.onFieldChange(fields.bondingCompanyPhone, e.currentTarget.value)}
                                        error={isSubmitted && fields.bondingCompanyPhone.error}
                                        {...fields.bondingCompanyPhone.attrs}
                                    />
                                </GridItem>
                            </>
                        )}
                        <GridItem width={12}>
                            <TextArea
                                value={fields.additionalInformation.value}
                                onChange={e => requestAddressForm?.onFieldChange(fields.additionalInformation, e.currentTarget.value)}
                                error={isSubmitted && fields.additionalInformation.error}
                                {...fields.additionalInformation.attrs}
                                rows={3}

                            />
                        </GridItem>
                    </>
                )}

                {errorMessage && (
                    <GridItem data-test-selector="requestAddress_ErrorMessage" {...styles.submitErrorGridItem}>
                        <Typography {...styles.submitErrorTitle}>{errorMessage}</Typography>
                    </GridItem>
                )}

                <GridItem {...styles.actionButtonsGridItem}>
                    <StyledWrapper {...styles.formButtonsWrapper}>
                        <Button
                            {...styles.cancelButton}
                            type="button"
                            onClick={onCancel}
                            data-test-selector="requestAddress_cancel"
                        >
                            {translate("Cancel")}
                        </Button>
                        <Button
                            {...styles.submitButton}
                            type="submit"
                            disabled={isSubmitting}
                            data-test-selector="requestAddress_createButton"
                        >
                            {translate("Submit")}
                        </Button>
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
        </StyledForm>
    );
};

export default connect(null, mapDispatchToProps)(RequestAddressForm);
